/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * WAVEFORM LINE — Smooth oscilloscope-style waveform display
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Renders the raw time-domain waveform data as a smooth, glowing line.
 * This is an "oscilloscope" — it shows the actual shape of the audio wave.
 *
 * HOW IT WORKS:
 * 1. `timeDomainData` contains amplitude values that oscillate around 128 (center).
 *    Values above 128 = positive wave, below = negative wave.
 * 2. We draw a continuous line across the canvas where X = time, Y = amplitude.
 * 3. The line glows using shadowBlur, pulsing brighter on beats.
 * 4. Color shifts based on the overall frequency content (bass = warm, treble = cool).
 */

import { Visualization, FrameData, VisualizationSetting } from '../../types';
import { BeatPulse, SmoothedValue } from '../../utils/audioUtils';
import { hsl } from '../../utils/colorUtils';

export class WaveformLine implements Visualization {
  id = 'waveform-line';
  name = 'Waveform Line';
  category = 'spectrum' as const;
  description = 'Smooth oscilloscope-style waveform with glow';
  renderer = 'canvas2d' as const;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private beatPulse = new BeatPulse(0.94);
  private smoothHue = new SmoothedValue(0.05);

  private lineWidth = 2.5;
  private glowAmount = 15;
  private colorHue = 160;  // base hue (cyan-ish)

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  render(frame: FrameData): void {
    const { timeDomainData, bassEnergy, trebleEnergy, isBeat, beatIntensity } = frame;
    const { width, height } = this.canvas;
    const ctx = this.ctx;
    const centerY = height / 2;

    this.beatPulse.update(isBeat, beatIntensity);

    // Hue shifts: bass pushes warm (red), treble pushes cool (blue)
    const targetHue = this.colorHue + bassEnergy * 40 - trebleEnergy * 30;
    this.smoothHue.update(targetHue);

    // Clear with slight trail for smooth movement
    ctx.fillStyle = 'rgba(0, 0, 0, 0.82)';
    ctx.fillRect(0, 0, width, height);

    // ── Draw the waveform ─────────────────────────────────────────────────
    const hue = this.smoothHue.value;
    const lightness = 55 + this.beatPulse.value * 25;
    const color = hsl(hue, 80, lightness);

    ctx.strokeStyle = color;
    ctx.lineWidth = this.lineWidth + this.beatPulse.value * 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowColor = color;
    ctx.shadowBlur = this.glowAmount + this.beatPulse.value * 20;

    ctx.beginPath();

    // We downsample the data — don't need to plot every single sample
    const step = Math.max(1, Math.floor(timeDomainData.length / width));
    for (let i = 0; i < timeDomainData.length; i += step) {
      // timeDomainData oscillates around 128 (center = silence)
      // Normalize to -1..+1 range, then scale to canvas height
      const normalized = (timeDomainData[i] - 128) / 128;
      const x = (i / timeDomainData.length) * width;
      const y = centerY + normalized * (height * 0.4);

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();

    // Reset shadow
    ctx.shadowBlur = 0;

    // ── Subtle center line ────────────────────────────────────────────────
    ctx.strokeStyle = hsl(hue, 30, 25, 0.3);
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(width, centerY);
    ctx.stroke();
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  destroy(): void {}

  getSettings(): VisualizationSetting[] {
    return [
      { key: 'lineWidth', label: 'Line Width', type: 'range', value: this.lineWidth, min: 1, max: 6, step: 0.5 },
      { key: 'glowAmount', label: 'Glow', type: 'range', value: this.glowAmount, min: 0, max: 40, step: 2 },
      { key: 'colorHue', label: 'Color Shift', type: 'range', value: this.colorHue, min: 0, max: 360, step: 10 },
    ];
  }

  setSettings(settings: Record<string, number | string | boolean>): void {
    if (typeof settings.lineWidth === 'number') this.lineWidth = settings.lineWidth;
    if (typeof settings.glowAmount === 'number') this.glowAmount = settings.glowAmount;
    if (typeof settings.colorHue === 'number') this.colorHue = settings.colorHue;
  }
}
